import { HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, switchMap, tap } from 'rxjs';

import { AuthService } from '@core/services/auth/auth';
import { isManagedApiUrl } from '@core/utils/api-url';
import { environment } from '@environment/environment';

const ANONYMOUS_ENDPOINTS = ['/v1/invitations/token/'];

// The engines' authorization gate marks its own refusals with this header, which a handler's 401 or 403
// never carries.
export const AUTHORIZATION_DENIED_HEADER = 'Yt-Authorization-Denied';

function isAnonymousRequest(url: string): boolean {
  return ANONYMOUS_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function isAuthorizationRefusal(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.headers?.get(AUTHORIZATION_DENIED_HEADER) === '1';
}

export function authInterceptorFn(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authService = inject(AuthService);

  if (isManagedApiUrl(req.url, environment.apiUrl) && !isAnonymousRequest(req.url)) {
    return from(authService.getAuthToken()).pipe(
      switchMap((token) => {
        let authReq = req;
        if (token) {
          authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`
            }
          });
        }
        return next(authReq);
      }),
      // A refusal may mean the token predates authority the session now holds, so it asks for a new one.
      // The refused request still fails to its caller: nothing is replayed.
      tap({
        error: (error: unknown) => {
          if (isAuthorizationRefusal(error)) {
            void authService.refreshAfterRefusal();
          }
        }
      })
    );
  } else {
    return next(req);
  }
}
